// Meta Pixel (Facebook Pixel) helper. Mirrors the pattern in
// src/lib/analytics.ts (GA4) so call-sites stay symmetrical:
//
//   import { trackMetaEvent } from "@/lib/metaPixel";
//   trackMetaEvent("Lead", { content_name: "newsletter" });
//
// The pixel script itself is loaded by the env-gated <script> block in
// index.html (the __META_PIXEL_BLOCK_START__/END__ region). When the
// VITE_META_PIXEL_ID Replit Secret is unset, the htmlSeoEnv plugin in
// vite.config.ts strips that entire region — so window.fbq simply never
// exists and every helper here becomes a no-op. No stub script ever ships.
//
// We deliberately omit `Purchase` from the standard-event union: there is
// no real paid checkout on jinglemission.com yet, and Meta's spec is clear
// that fake conversion events skew ads-manager attribution. Add Purchase
// when (and only when) a real billing flow lands.

import { getAttributionPayload, getTestModeFlags } from "@/lib/utm";

declare global {
  interface Window {
    fbq?: {
      (...args: unknown[]): void;
      queue?: unknown[];
      loaded?: boolean;
      version?: string;
      callMethod?: (...args: unknown[]) => void;
    };
    _fbq?: unknown;
  }
}

export type MetaPixelStandardEvent =
  | "PageView"
  | "ViewContent"
  | "Lead"
  | "CompleteRegistration";

// Fire a Meta Pixel event safely. No-ops when:
//   - Running on the server (typeof window === "undefined")
//   - The pixel never loaded (window.fbq missing) — happens when
//     VITE_META_PIXEL_ID is unset, the visitor is offline, or an
//     ad-blocker stripped the script.
// Never throws — analytics must never break the page.
export function trackMetaEvent(
  event: MetaPixelStandardEvent,
  params?: Record<string, string | number | boolean>,
): void {
  if (typeof window === "undefined") return;
  const fbq = window.fbq;
  if (typeof fbq !== "function") return;
  try {
    // Attribution + test-mode flags merged first; caller params win.
    const enriched = {
      ...getAttributionPayload(),
      ...getTestModeFlags(),
      ...(params ?? {}),
    };
    if (Object.keys(enriched).length > 0) {
      fbq("track", event, enriched);
    } else {
      fbq("track", event);
    }
  } catch {
    /* swallow analytics errors */
  }
}

// Fire a Meta Pixel PageView. The first PageView is already auto-emitted
// by the inline `fbq('track','PageView')` in index.html, so this should
// only be called from an SPA route-change effect AFTER the very first
// mount — otherwise the landing page is double-counted.
export function metaPageView(): void {
  trackMetaEvent("PageView");
}
